import React from 'react';

const TermsContent: React.FC = () => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      <section>
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>Work in Progress Disclaimer</h2>
        <p>
          PlatefullAI is an evolving platform. Our dietary classifications, ingredient analyses,
          and recommendations are continuously improving and may contain mistakes. We encourage you to
          help us find and correct errors so that over time you can rely on them with confidence.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>No Medical or Allergy Advice</h2>
        <p>
          PlatefullAI does not provide medical advice and should not be used as a substitute for
          professional diagnosis or treatment. If you have allergies, medical conditions, or strict
          dietary requirements, you must independently verify any information before consuming a product or dish.
        </p>
        <p>
          In particular, do not rely solely on PlatefullAI for allergy safety, disease management,
          or therapeutic diets. Always double-check our assumptions and contact us when we are wrong—
          we depend on your feedback to improve the system.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>Restaurant-Provided Information</h2>
        <p>
          We rely on restaurants and venues to provide accurate ingredient and preparation information.
          Mistakes can occur (for example, a missing or extra digit in nutrition data, or outdated ingredient lists).
          If something looks incorrect, please verify with the venue directly and report it to us so we can correct it.
        </p>
        <p>
          Restaurants may also interpret dietary categories differently and may not be aware of nuanced distinctions
          (e.g., vegan vs. vegetarian, or cross-contact considerations for gluten). We provide guidance to partners,
          but this is not foolproof and should be independently verified when it matters.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>Legacy Menus During Platform Updates</h2>
        <p>
          We are updating how menus are represented on our platform. During this transition, many existing menus may
          only include high-level tags such as Vegan, Vegetarian, or Gluten-Free without deeper ingredient-level details.
          Please bear with us as we launch, grow, and continuously update to provide more granular information over time.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>Accuracy and Improvements</h2>
        <p>
          While we strive for accuracy, classification errors can occur. We will gradually dial back
          cautionary language when we have high confidence in specific areas of the platform; this will
          take time and iteration.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: '22px', fontWeight: 800, margin: '8px 0 4px' }}>For General Users</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p>
            Your data will not be shared with third-party apps. Even if an integrated app appears to
            "know" your preferences, it does not have access to your personal data. Integrated partners
            are prohibited from attempting to capture or store your personal dietary data obtained via
            PlatefullAI.
          </p>
          <p>
            Treat our classifications as guidance only. Double-check allergy-sensitive or medical-critical
            information with the venue and/or a professional. Report inaccuracies so we can correct them quickly.
          </p>
        </div>
      </section>

      <section>
        <h2 style={{ fontSize: '22px', fontWeight: 800, margin: '8px 0 4px' }}>For API/SDK Users</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p>
            You are expressly prohibited from saving, caching, or otherwise persisting any user data inferred
            from our services. Use such data only transiently to fulfill a user request, and discard it immediately
            after use. Attempting to capture user data from PlatefullAI is prohibited and may result in suspension
            or termination of access.
          </p>
          <p>
            You must clearly communicate limitations to your users and avoid representing PlatefullAI output as
            medical advice. If you display restaurant data, you are responsible for surfacing caveats where venue
            information may be incomplete or outdated.
          </p>
        </div>
      </section>

      <section>
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, PlatefullAI and its affiliates shall not be liable for
          any indirect, incidental, special, consequential, or exemplary damages arising from or relating
          to your use of the platform, including reliance on any classifications or recommendations.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>Changes to These Terms</h2>
        <p>
          We may update these Terms & Conditions from time to time. Material changes will be reflected on
          this page with an updated effective date. Continued use of the platform constitutes acceptance
          of the updated terms.
        </p>
      </section>

      <section>
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>Contact</h2>
        <p>
          Questions or feedback? Contact us at <a href="mailto:support@platefullai.com">support@platefullai.com</a>.
        </p>
      </section>
    </div>
  );
};

export default TermsContent;


